import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet, Ticket, CreditCard, MinusCircle, Plus, AlertTriangle, CheckCircle2, XCircle, Trash2, Edit3, Send, Phone, Clock } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle, btnStyle, statCard } from '../theme';

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const BUCKET_COLORS = {
    'current':  { bg:'#d1fae5', color:'#065f46' },
    '0-30':     { bg:'#fef3c7', color:'#92400e' },
    '31-60':    { bg:'#fed7aa', color:'#9a3412' },
    '61-90':    { bg:'#fecaca', color:'#991b1b' },
    '90+':      { bg:'#fee2e2', color:'#991b1b' },
};

export default function FinancialControlsPage({ API_BASE, patientsList, currentUser }) {
    const [tab, setTab] = useState('ageing');

    const tabBtn = (k, Icon, label) => (
        <button onClick={()=>setTab(k)} style={{padding:'9px 16px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
            backgroundColor: tab===k ? theme.primary : '#e9ecef',
            color: tab===k ? 'white' : '#555',
            display:'inline-flex', alignItems:'center', gap:'6px'}}>
            <Icon size={14}/>{label}
        </button>
    );

    return (
        <div>
            <div style={{marginBottom:'18px'}}>
                <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Financial Controls</h2>
                <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>Ageing · Coupons · Installments · Write-offs</p>
            </div>

            <div style={{display:'flex', gap:'8px', marginBottom:'18px', flexWrap:'wrap'}}>
                {tabBtn('ageing',   Wallet,       'Ageing & Credit')}
                {tabBtn('coupons',  Ticket,       'Coupons')}
                {tabBtn('installments', CreditCard,'Installments / EMI')}
                {tabBtn('writeoffs', MinusCircle, 'Write-offs')}
            </div>

            {tab === 'ageing'        && <AgeingTab        API_BASE={API_BASE} patientsList={patientsList}/>}
            {tab === 'coupons'       && <CouponsTab       API_BASE={API_BASE} currentUser={currentUser}/>}
            {tab === 'installments'  && <InstallmentsTab  API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser}/>}
            {tab === 'writeoffs'     && <WriteOffsTab     API_BASE={API_BASE} currentUser={currentUser}/>}
        </div>
    );
}

// ─── Ageing Tab ───────────────────────────────────────────────────────────
function AgeingTab({ API_BASE, patientsList }) {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [editing, setEditing] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [a, s] = await Promise.all([
                axios.get(`${API_BASE}/credit/ageing`),
                axios.get(`${API_BASE}/credit/ageing/summary`),
            ]);
            setRows(a.data); setSummary(s.data);
        } catch {}
    }, [API_BASE]);
    useEffect(() => { refresh(); }, [refresh]);

    const saveLimit = async () => {
        await axios.put(`${API_BASE}/credit/patient/${editing._id}`, {
            creditLimit: editing.creditLimit,
            creditTermsDays: editing.creditTermsDays,
        });
        setEditing(null); refresh();
    };

    return (
        <div>
            {summary && (
                <div style={{display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:'12px', marginBottom:'18px'}}>
                    {[
                        { l:'Current',    v:summary.current, c:theme.success   },
                        { l:'0-30 days',  v:summary['0-30'],  c:theme.warning  },
                        { l:'31-60',      v:summary['31-60'], c:'#fb923c'      },
                        { l:'61-90',      v:summary['61-90'], c:'#ef4444'      },
                        { l:'90+ days',   v:summary['90+'],   c:theme.danger   },
                        { l:'Total',      v:summary.grand,    c:theme.navy     },
                    ].map((s,i) => (
                        <div key={i} style={statCard}>
                            <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                            <h2 style={{margin:'4px 0 0', color:s.c, fontSize:'1.2rem', fontWeight:'700'}}>{fmtINR(s.v)}</h2>
                        </div>
                    ))}
                </div>
            )}

            <div style={cardStyle}>
                <h3 style={{margin:'0 0 12px', color:theme.navy, fontSize:'1rem'}}>Patient Outstanding ({rows.length})</h3>
                {rows.length === 0 ? (
                    <div style={{textAlign:'center', padding:'40px', color:theme.textMuted}}>No outstanding balances</div>
                ) : (
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Patient</th>
                            <th style={thStyle}>Outstanding</th>
                            <th style={thStyle}>Bucket</th>
                            <th style={thStyle}>Oldest (days)</th>
                            <th style={thStyle}>Credit Limit</th>
                            <th style={thStyle}>Terms</th>
                            <th style={thStyle}>Actions</th>
                        </tr></thead>
                        <tbody>
                            {rows.map(r => {
                                const bc = BUCKET_COLORS[r.bucket] || {};
                                const overLimit = r.creditLimit > 0 && r.total > r.creditLimit;
                                return (
                                    <tr key={r.patientId}>
                                        <td style={tdStyle}>
                                            <strong>{r.patientName}</strong>
                                            <div style={{fontSize:'0.72rem', color:theme.textMuted}}>{r.uhid} · {r.phone}</div>
                                        </td>
                                        <td style={tdStyle}><strong style={{color: overLimit ? theme.danger : theme.navy}}>{fmtINR(r.total)}</strong>{overLimit && <div style={{fontSize:'0.7rem', color:theme.danger, fontWeight:'700'}}>OVER LIMIT</div>}</td>
                                        <td style={tdStyle}><span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700', backgroundColor:bc.bg||'#f1f5f9', color:bc.color||'#475569'}}>{r.bucket}</span></td>
                                        <td style={tdStyle}>{r.maxDaysOverdue > 0 ? `${r.maxDaysOverdue}d` : '—'}</td>
                                        <td style={tdStyle}>{r.creditLimit ? fmtINR(r.creditLimit) : <em style={{color:'#cbd5e1'}}>no limit</em>}</td>
                                        <td style={tdStyle}>{r.creditTermsDays ? `${r.creditTermsDays}d` : '—'}</td>
                                        <td style={tdStyle}>
                                            <button onClick={()=>setEditing({ _id:r.patientId, name:r.patientName, creditLimit:r.creditLimit, creditTermsDays:r.creditTermsDays })}
                                                style={{padding:'4px 10px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Set Limit</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <Modal onClose={()=>setEditing(null)} title={`Credit Policy — ${editing.name}`}>
                    <label style={labelStyle}>Credit Limit (₹) — 0 = no limit</label>
                    <input type="number" value={editing.creditLimit||0} onChange={e=>setEditing({...editing, creditLimit:Number(e.target.value)||0})} style={inputStyle}/>
                    <label style={labelStyle}>Net Terms (days) — 0 = due immediately</label>
                    <input type="number" value={editing.creditTermsDays||0} onChange={e=>setEditing({...editing, creditTermsDays:Number(e.target.value)||0})} style={inputStyle}/>
                    <button onClick={saveLimit} style={{...btnStyle, marginTop:'12px'}}>Save</button>
                </Modal>
            )}
        </div>
    );
}

// ─── Coupons Tab ──────────────────────────────────────────────────────────
function CouponsTab({ API_BASE, currentUser }) {
    const [coupons, setCoupons] = useState([]);
    const [editing, setEditing] = useState(null);

    const empty = () => ({
        code:'', name:'', description:'', discountType:'percent', discountValue:10,
        maxDiscount:0, minBillAmount:0, applicableToTypes:[], maxUsesTotal:0, maxUsesPerPatient:1,
        validFrom:'', validTo:'', status:'active',
    });

    const refresh = useCallback(async () => {
        try { const r = await axios.get(`${API_BASE}/coupons`); setCoupons(r.data); } catch {}
    }, [API_BASE]);
    useEffect(() => { refresh(); }, [refresh]);

    const save = async () => {
        try {
            const body = { ...editing, createdBy: currentUser?.username || '' };
            if (editing._id) await axios.put(`${API_BASE}/coupons/${editing._id}`, body);
            else await axios.post(`${API_BASE}/coupons`, body);
            setEditing(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
    };

    const del = async (id) => {
        if (!window.confirm('Deactivate this coupon?')) return;
        await axios.delete(`${API_BASE}/coupons/${id}`);
        refresh();
    };

    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'12px'}}>
                <h3 style={{margin:0, color:theme.navy}}>Coupons ({coupons.length})</h3>
                <button onClick={()=>setEditing(empty())} style={{...btnStyle, marginTop:0, padding:'8px 14px', fontSize:'0.85rem'}}><Plus size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>New Coupon</button>
            </div>
            <div style={cardStyle}>
                {coupons.length === 0 ? (
                    <div style={{textAlign:'center', padding:'40px', color:theme.textMuted}}>No coupons yet</div>
                ) : (
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Code</th>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Discount</th>
                            <th style={thStyle}>Valid</th>
                            <th style={thStyle}>Uses</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}></th>
                        </tr></thead>
                        <tbody>
                            {coupons.map(c => (
                                <tr key={c._id}>
                                    <td style={tdStyle}><strong style={{fontFamily:'monospace'}}>{c.code}</strong></td>
                                    <td style={tdStyle}>{c.name||'—'}</td>
                                    <td style={tdStyle}>{c.discountType==='flat' ? fmtINR(c.discountValue) : `${c.discountValue}%${c.maxDiscount?` (max ${fmtINR(c.maxDiscount)})`:''}`}</td>
                                    <td style={tdStyle}><span style={{fontSize:'0.76rem', color:theme.textMuted}}>{fmtDate(c.validFrom)} → {fmtDate(c.validTo)}</span></td>
                                    <td style={tdStyle}>{c.usesCount||0}{c.maxUsesTotal?` / ${c.maxUsesTotal}`:''}</td>
                                    <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:c.status==='active'?'#d1fae5':'#e5e7eb', color:c.status==='active'?'#065f46':'#374151'}}>{c.status}</span></td>
                                    <td style={tdStyle}>
                                        <button onClick={()=>setEditing({...c, validFrom:c.validFrom?c.validFrom.slice(0,10):'', validTo:c.validTo?c.validTo.slice(0,10):''})} style={{padding:'4px 10px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', marginRight:'4px'}}><Edit3 size={12}/></button>
                                        <button onClick={()=>del(c._id)} style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem'}}><Trash2 size={12}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <Modal onClose={()=>setEditing(null)} title={editing._id ? 'Edit Coupon' : 'New Coupon'}>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                        <div><label style={labelStyle}>Code *</label><input value={editing.code} onChange={e=>setEditing({...editing, code:e.target.value.toUpperCase()})} style={{...inputStyle, fontFamily:'monospace'}}/></div>
                        <div><label style={labelStyle}>Name</label><input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Discount Type</label>
                            <select value={editing.discountType} onChange={e=>setEditing({...editing, discountType:e.target.value})} style={inputStyle}>
                                <option value="percent">Percent (%)</option>
                                <option value="flat">Flat (₹)</option>
                            </select>
                        </div>
                        <div><label style={labelStyle}>Value {editing.discountType==='percent'?'(%)':'(₹)'}</label><input type="number" value={editing.discountValue} onChange={e=>setEditing({...editing, discountValue:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Max Discount (₹ cap for %)</label><input type="number" value={editing.maxDiscount} onChange={e=>setEditing({...editing, maxDiscount:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Min Bill Amount (₹)</label><input type="number" value={editing.minBillAmount} onChange={e=>setEditing({...editing, minBillAmount:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Valid From</label><input type="date" value={editing.validFrom||''} onChange={e=>setEditing({...editing, validFrom:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Valid To</label><input type="date" value={editing.validTo||''} onChange={e=>setEditing({...editing, validTo:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Max Total Uses (0=∞)</label><input type="number" value={editing.maxUsesTotal} onChange={e=>setEditing({...editing, maxUsesTotal:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Max Uses / Patient</label><input type="number" value={editing.maxUsesPerPatient} onChange={e=>setEditing({...editing, maxUsesPerPatient:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div style={{gridColumn:'span 2'}}><label style={labelStyle}>Description</label><input value={editing.description||''} onChange={e=>setEditing({...editing, description:e.target.value})} style={inputStyle}/></div>
                    </div>
                    <button onClick={save} style={{...btnStyle, marginTop:'14px'}}>{editing._id ? 'Update' : 'Create'}</button>
                </Modal>
            )}
        </div>
    );
}

// ─── Installments Tab ─────────────────────────────────────────────────────
function InstallmentsTab({ API_BASE, patientsList, currentUser }) {
    const [plans, setPlans] = useState([]);
    const [creating, setCreating] = useState(null);
    const [bills, setBills] = useState([]);

    const refresh = useCallback(async () => {
        try { const r = await axios.get(`${API_BASE}/installments`); setPlans(r.data); } catch {}
    }, [API_BASE]);
    useEffect(() => { refresh(); }, [refresh]);

    const startCreate = async () => {
        try { const r = await axios.get(`${API_BASE}/bills?status=finalized&limit=500`); setBills(r.data); }
        catch { setBills([]); }
        setCreating({ patientId:'', billId:'', totalAmount:0, installments:6, frequency:'monthly', startDate:new Date().toISOString().slice(0,10), notes:'' });
    };

    const save = async () => {
        try {
            await axios.post(`${API_BASE}/installments`, { ...creating, createdBy: currentUser?.username || '' });
            setCreating(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };

    const markPaid = async (planId, idx) => {
        if (!window.confirm('Mark this installment as paid?')) return;
        await axios.put(`${API_BASE}/installments/${planId}/installment/${idx}/pay`, {});
        refresh();
    };

    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'12px'}}>
                <h3 style={{margin:0, color:theme.navy}}>EMI Plans ({plans.length})</h3>
                <button onClick={startCreate} style={{...btnStyle, marginTop:0, padding:'8px 14px', fontSize:'0.85rem'}}><Plus size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>New Plan</button>
            </div>

            <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                {plans.length === 0 && <div style={{...cardStyle, textAlign:'center', color:theme.textMuted, padding:'40px'}}>No installment plans yet</div>}
                {plans.map(p => (
                    <div key={p._id} style={cardStyle}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                            <div>
                                <strong style={{color:theme.navy}}>{p.patientId?.name || '—'}</strong>
                                <span style={{marginLeft:'8px', fontSize:'0.82rem', color:theme.textMuted}}>{p.billId?.billNumber} · {fmtINR(p.totalAmount)} · {p.installments} {p.frequency}</span>
                            </div>
                            <span style={{padding:'3px 10px', borderRadius:'10px', fontSize:'0.74rem', fontWeight:'700', backgroundColor: p.status==='completed' ? '#d1fae5' : p.status==='cancelled' ? '#e5e7eb' : '#dbeafe', color: p.status==='completed' ? '#065f46' : p.status==='cancelled' ? '#374151' : '#1e40af'}}>{p.status}</span>
                        </div>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:'8px'}}>
                            {p.schedule.map((r, i) => {
                                const colors = { pending:{bg:'#f1f5f9',color:'#475569'}, paid:{bg:'#d1fae5',color:'#065f46'}, overdue:{bg:'#fee2e2',color:'#991b1b'}, waived:{bg:'#e5e7eb',color:'#374151'} };
                                const col = colors[r.status] || colors.pending;
                                return (
                                    <div key={i} style={{padding:'10px', borderRadius:'8px', border:`1px solid ${col.color}44`, backgroundColor:col.bg}}>
                                        <div style={{fontSize:'0.7rem', color:col.color, fontWeight:'700', textTransform:'uppercase'}}>#{i+1} · {r.status}</div>
                                        <div style={{fontWeight:'700', color:theme.navy, fontSize:'0.92rem', margin:'4px 0'}}>{fmtINR(r.amount)}</div>
                                        <div style={{fontSize:'0.72rem', color:theme.textMuted}}>{fmtDate(r.dueDate)}</div>
                                        {r.status !== 'paid' && r.status !== 'waived' && (
                                            <button onClick={()=>markPaid(p._id, i)} style={{marginTop:'6px', padding:'4px 10px', width:'100%', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'600'}}>Mark Paid</button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {creating && (
                <Modal onClose={()=>setCreating(null)} title="New EMI Plan">
                    <label style={labelStyle}>Patient</label>
                    <select value={creating.patientId} onChange={e=>setCreating({...creating, patientId:e.target.value, billId:''})} style={inputStyle}>
                        <option value="">Select patient…</option>
                        {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
                    </select>
                    <label style={labelStyle}>Finalized Bill</label>
                    <select value={creating.billId} onChange={e=>{ const b = bills.find(x=>x._id===e.target.value); setCreating(c=>({...c, billId:e.target.value, totalAmount:b?.balanceAmount||b?.totalAmount||0})); }} style={inputStyle}>
                        <option value="">Select bill…</option>
                        {bills.filter(b => !creating.patientId || b.patientId === creating.patientId).map(b => (
                            <option key={b._id} value={b._id}>{b.billNumber} · {fmtINR(b.balanceAmount || b.totalAmount)}</option>
                        ))}
                    </select>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px'}}>
                        <div><label style={labelStyle}>Total Amount</label><input type="number" value={creating.totalAmount} onChange={e=>setCreating({...creating, totalAmount:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}># Installments</label><input type="number" min="1" value={creating.installments} onChange={e=>setCreating({...creating, installments:Number(e.target.value)||1})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Frequency</label>
                            <select value={creating.frequency} onChange={e=>setCreating({...creating, frequency:e.target.value})} style={inputStyle}>
                                <option value="weekly">Weekly</option>
                                <option value="fortnightly">Fortnightly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>
                        <div style={{gridColumn:'span 3'}}><label style={labelStyle}>Start Date</label><input type="date" value={creating.startDate} onChange={e=>setCreating({...creating, startDate:e.target.value})} style={inputStyle}/></div>
                    </div>
                    <button onClick={save} style={{...btnStyle, marginTop:'14px'}}>Create Plan</button>
                </Modal>
            )}
        </div>
    );
}

// ─── Write-offs Tab ───────────────────────────────────────────────────────
function WriteOffsTab({ API_BASE, currentUser }) {
    const [list, setList] = useState([]);
    const [statusFilter, setStatusFilter] = useState('pending');

    const refresh = useCallback(async () => {
        try { const r = await axios.get(`${API_BASE}/write-offs${statusFilter ? `?status=${statusFilter}` : ''}`); setList(r.data); } catch {}
    }, [API_BASE, statusFilter]);
    useEffect(() => { refresh(); }, [refresh]);

    const approve = async (id) => {
        if (!window.confirm('Approve this write-off? Bill balance will be reduced accordingly.')) return;
        await axios.put(`${API_BASE}/write-offs/${id}/approve`, { approvedBy: currentUser?.username || '' });
        refresh();
    };
    const reject = async (id) => {
        const reason = window.prompt('Reject reason:'); if (!reason) return;
        await axios.put(`${API_BASE}/write-offs/${id}/reject`, { rejectedBy: currentUser?.username || '', reason });
        refresh();
    };

    return (
        <div>
            <div style={{display:'flex', gap:'8px', marginBottom:'12px'}}>
                {['pending','approved','rejected',''].map(s => (
                    <button key={s} onClick={()=>setStatusFilter(s)}
                        style={{padding:'6px 14px', borderRadius:'16px', border:`1px solid ${statusFilter===s?theme.primary:theme.border}`, cursor:'pointer', fontSize:'0.78rem', fontWeight:'600',
                            backgroundColor: statusFilter===s ? theme.primary : 'white', color: statusFilter===s ? 'white' : '#555'}}>
                        {s || 'All'}
                    </button>
                ))}
            </div>
            <div style={cardStyle}>
                {list.length === 0 ? (
                    <div style={{textAlign:'center', padding:'40px', color:theme.textMuted}}>No write-offs</div>
                ) : (
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Requested</th>
                            <th style={thStyle}>Bill</th>
                            <th style={thStyle}>Patient</th>
                            <th style={thStyle}>Amount</th>
                            <th style={thStyle}>Category</th>
                            <th style={thStyle}>Reason</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}></th>
                        </tr></thead>
                        <tbody>
                            {list.map(w => (
                                <tr key={w._id}>
                                    <td style={tdStyle}>{fmtDate(w.requestedAt)}<div style={{fontSize:'0.72rem', color:theme.textMuted}}>{w.requestedBy}</div></td>
                                    <td style={tdStyle}><strong>{w.billId?.billNumber || '—'}</strong></td>
                                    <td style={tdStyle}>{w.patientId?.name || '—'}</td>
                                    <td style={tdStyle}><strong>{fmtINR(w.amount)}</strong></td>
                                    <td style={tdStyle}>{w.category}</td>
                                    <td style={tdStyle}><span style={{fontSize:'0.8rem'}}>{w.reason}</span></td>
                                    <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:{pending:'#fef3c7',approved:'#d1fae5',rejected:'#fee2e2'}[w.status], color:{pending:'#92400e',approved:'#065f46',rejected:'#991b1b'}[w.status]}}>{w.status}</span></td>
                                    <td style={tdStyle}>
                                        {w.status === 'pending' && (
                                            <div style={{display:'flex', gap:'4px'}}>
                                                <button onClick={()=>approve(w._id)} style={{padding:'4px 10px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'600'}}><CheckCircle2 size={12}/></button>
                                                <button onClick={()=>reject(w._id)} style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'600'}}><XCircle size={12}/></button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// ─── Shared Modal ─────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
    return (
        <div style={{position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={onClose}>
            <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', borderRadius:'14px', padding:'24px 28px', width:'540px', maxWidth:'95vw', maxHeight:'90vh', overflow:'auto'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
                    <h3 style={{margin:0, color:theme.navy}}>{title}</h3>
                    <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer'}}><XCircle size={20} color={theme.textMuted}/></button>
                </div>
                {children}
            </div>
        </div>
    );
}
